/*!
 * ua.select.multiple
 * Generic multi-select control for dialogs, wizards, and dashboards.
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.select.multiple",
  title: "Multi Select",
  description: "Select one or more options from a list with optional search and preview rendering.",
  control: true,
  preview: "ua.preview.select.multiple",
  supportServer: false,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Form field name.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Control title."
    },
    description: {
      title: "Description",
      type: "inner",
      description: "Helper text."
    },
    required: {
      title: "Required",
      type: "boolean",
      description: "Whether at least one value is required."
    },
    searchable: {
      title: "Searchable",
      type: "boolean",
      description: "Show a search box above options.",
      default: true
    },
    preview: {
      title: "Preview",
      type: "string",
      description: "Option rendering mode: text | tags | file.",
      list: ["text", "tags", "file"],
      default: "text"
    },
    options: {
      title: "Options",
      type: "array",
      of: "object",
      description: "Option rows: { value, label, description, icon, thumbnail, meta, badge }."
    },
    "options-json": {
      title: "Options JSON",
      type: "string",
      description: "JSON string for options rows. Use when object arrays must be passed through UA/JSON safely."
    },
    value: {
      title: "Value",
      type: "any",
      description: "Selected values (array or comma string)."
    }
  }
});

function normalizeOptions(options) {
  var rows = options instanceof Array ? options : [];
  var out = [];

  for (var i = 0; i < rows.length; i++) {
    var row = rows[i] || {};
    var value = (row.value || "").toString().trim();
    if (!value) continue;
    out.push({
      value: value,
      label: (row.label || row.value || "").toString(),
      description: (row.description || "").toString(),
      icon: (row.icon || "").toString(),
      thumbnail: (row.thumbnail || row.image || "").toString(),
      meta: (row.meta || "").toString(),
      badge: (row.badge || "").toString()
    });
  }

  return out;
}

function normalizeOptionsFromInput(options, optionsJson) {
  if (options instanceof Array && options.length > 0) return normalizeOptions(options);
  var raw = (optionsJson || "").toString().trim();
  if (!raw) return [];
  try {
    var parsed = JSON.parse(raw);
    return normalizeOptions(parsed);
  } catch (error) {
    console.warn("ua.select.multiple: invalid options-json payload.", {
      error: error
    });
    return [];
  }
}

function normalizeSelected(value) {
  if (value instanceof Array) {
    return value.map(function(item) {
      return (item || "").toString().trim();
    }).filter(Boolean);
  }

  var raw = (value || "").toString().trim();
  if (!raw) return [];
  return raw.split(",").map(function(item) {
    return (item || "").toString().trim();
  }).filter(Boolean);
}

function optionMarkup(mode, option) {
  var label = option.label || option.value;
  if (mode === "tags") {
    return "<span class='badge bg-secondary-subtle text-body border'>" + escapeHtml(label) + "</span>";
  }

  if (mode === "file") {
    var thumb = option.thumbnail ? ("<img src='" + escapeHtml(option.thumbnail) + "' alt='' style='width:2rem;height:2rem;object-fit:cover;border-radius:.35rem;'/>") : "";
    var iconClass = option.icon ? (option.icon.indexOf("bi-") === 0 ? ("bi " + option.icon) : option.icon) : "bi bi-file-earmark";
    var icon = thumb || ("<span class='text-muted'><i class='" + escapeHtml(iconClass) + "'></i></span>");
    return "<div class='d-flex align-items-center gap-2'>" +
      icon +
      "<div class='d-flex flex-column'>" +
      "<span>" + escapeHtml(label) + "</span>" +
      (option.meta ? ("<small class='text-muted'>" + escapeHtml(option.meta) + "</small>") : "") +
      "</div>" +
      (option.badge ? ("<span class='badge bg-secondary-subtle text-body border ms-2'>" + escapeHtml(option.badge) + "</span>") : "") +
      "</div>";
  }

  return "<div class='d-flex flex-column'>" +
    "<span>" + escapeHtml(label) + "</span>" +
    (option.description ? ("<small class='text-muted'>" + escapeHtml(option.description) + "</small>") : "") +
    "</div>";
}

function escapeHtml(str) {
  return (str || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

exports.render = async function(options) {
  var name = (options.name || "").toString().trim();
  var title = (options.title || "").toString().trim();
  var description = (options.description || "").toString().trim();
  var required = options.required === true;
  var searchable = options.searchable !== false;
  var preview = (options.preview || "text").toString().trim().toLowerCase();
  var optionRows = normalizeOptionsFromInput(options.options || [], options["options-json"] || "");
  var selected = normalizeSelected(options.value);
  var showSearch = searchable && optionRows.length > 5;

  var wrapper = document.createElement("div");
  wrapper.className = "form-group pb-3 ua-select-multiple";
  wrapper.setAttribute("name", name);
  wrapper.setAttribute("data-required", required ? "true" : "false");
  wrapper.setAttribute("data-preview-mode", preview);

  if (title) {
    var label = document.createElement("label");
    label.className = "form-label fw-semibold";
    label.textContent = title;
    wrapper.appendChild(label);
  }

  if (description) {
    var help = document.createElement("div");
    help.className = "form-text mb-2";
    help.textContent = description;
    wrapper.appendChild(help);
  }

  var selectedMap = {};
  for (var si = 0; si < selected.length; si++) {
    selectedMap[selected[si]] = true;
  }

  var selectedHost = document.createElement("div");
  selectedHost.className = "d-flex flex-wrap gap-1 mb-2";
  selectedHost.setAttribute("data-multi-selected", "true");

  var searchInput = null;
  if (showSearch) {
    var searchWrap = document.createElement("div");
    searchWrap.className = "d-flex align-items-center gap-2 mb-2";
    wrapper.appendChild(searchWrap);

    searchInput = document.createElement("input");
    searchInput.type = "search";
    searchInput.className = "form-control form-control-sm";
    searchInput.placeholder = "Search...";
    searchWrap.appendChild(searchInput);
  }

  // Keep selected items below search and above the options list.
  wrapper.appendChild(selectedHost);

  var list = document.createElement("div");
  list.className = "d-flex flex-column gap-1";
  wrapper.appendChild(list);

  for (var i = 0; i < optionRows.length; i++) {
    var row = optionRows[i];
    var item = document.createElement("label");
    item.className = "d-flex align-items-start gap-2 border rounded p-2";
    item.setAttribute("data-multi-option", row.value.toLowerCase());

    var check = document.createElement("input");
    check.type = "checkbox";
    check.className = "visually-hidden";
    check.setAttribute("data-multi-check", "true");
    check.value = row.value;
    check.name = name;
    check.checked = !!selectedMap[row.value];

    var body = document.createElement("div");
    body.className = "flex-grow-1";
    body.innerHTML = optionMarkup(preview, row);

    item.appendChild(check);
    item.appendChild(body);
    list.appendChild(item);
  }

  if (searchInput) {
    searchInput.addEventListener("input", function() {
      var q = (searchInput.value || "").toString().trim().toLowerCase();
      var items = list.querySelectorAll("[data-multi-option]");
      for (var i = 0; i < items.length; i++) {
        var target = items[i];
        var hay = ((target.textContent || "") + " " + (target.getAttribute("data-multi-option") || "")).toLowerCase();
        target.classList.toggle("d-none", !!q && hay.indexOf(q) < 0);
      }
    });
  }

  function renderSelected() {
    selectedHost.innerHTML = "";
    var checks = list.querySelectorAll("input[data-multi-check='true']:checked");

    if (checks.length > 2) {
      var clearAllBtn = document.createElement("button");
      clearAllBtn.type = "button";
      clearAllBtn.className = "btn btn-sm btn-outline-secondary";
      clearAllBtn.setAttribute("data-multi-clear-all", "true");
      clearAllBtn.innerHTML = "<i class='bi bi-eraser'></i>";
      selectedHost.appendChild(clearAllBtn);
    }

    for (var i = 0; i < checks.length; i++) {
      var value = (checks[i].value || "").toString().trim();
      if (!value) continue;
      var row = null;
      for (var r = 0; r < optionRows.length; r++) {
        if (optionRows[r].value === value) {
          row = optionRows[r];
          break;
        }
      }
      var labelText = (row && row.label) ? row.label : value;

      var chipBtn = document.createElement("button");
      chipBtn.type = "button";
      chipBtn.className = "btn btn-sm btn-outline-secondary";
      chipBtn.setAttribute("data-multi-remove", value);
      chipBtn.textContent = labelText;
      selectedHost.appendChild(chipBtn);
    }
  }

  function syncOptionStyles() {
    var items = list.querySelectorAll("[data-multi-option]");
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var cb = item.querySelector("input[data-multi-check='true']");
      var isChecked = !!(cb && cb.checked);
      item.classList.toggle("border-primary", isChecked);
      item.classList.toggle("text-bg-primary", isChecked);
      item.classList.toggle("text-body", !isChecked);
    }
  }

  list.addEventListener("change", function(event) {
    var target = event.target;
    if (!target || target.getAttribute("data-multi-check") !== "true") return;
    syncOptionStyles();
    renderSelected();
  });

  selectedHost.addEventListener("click", function(event) {
    var clearAll = event.target.closest("[data-multi-clear-all='true']");
    if (clearAll) {
      var allChecks = list.querySelectorAll("input[data-multi-check='true']");
      for (var c = 0; c < allChecks.length; c++) {
        allChecks[c].checked = false;
      }
      syncOptionStyles();
      renderSelected();
      return;
    }

    var removeBtn = event.target.closest("[data-multi-remove]");
    if (!removeBtn) return;
    var value = (removeBtn.getAttribute("data-multi-remove") || "").toString().trim();
    if (!value) return;
    var checks = list.querySelectorAll("input[data-multi-check='true']");
    for (var i = 0; i < checks.length; i++) {
      if ((checks[i].value || "").toString() === value) {
        checks[i].checked = false;
      }
    }
    syncOptionStyles();
    renderSelected();
  });

  syncOptionStyles();
  renderSelected();

  var invalid = document.createElement("div");
  invalid.className = "invalid-feedback d-block";
  invalid.setAttribute("data-multi-invalid", "true");
  invalid.textContent = "";
  wrapper.appendChild(invalid);

  return wrapper;
};

async function save(name, control, repo) {
  var required = control.getAttribute("data-required") === "true";
  var checks = control.querySelectorAll("input[data-multi-check='true']:checked");
  var selected = [];
  for (var i = 0; i < checks.length; i++) {
    var value = (checks[i].value || "").toString().trim();
    if (!value) continue;
    if (selected.indexOf(value) >= 0) continue;
    selected.push(value);
  }

  var invalid = control.querySelector("[data-multi-invalid='true']");
  if (required && selected.length === 0) {
    repo.success = false;
    repo.errors = [{
      type: "validation",
      message: "At least one option is required."
    }];
    if (invalid) invalid.textContent = "At least one option is required.";
    control.classList.add("is-invalid");
    repo.data = [];
    return repo;
  }

  if (invalid) invalid.textContent = "";
  control.classList.remove("is-invalid");
  repo.success = true;
  repo.data = selected;
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var selected = normalizeSelected(data);
  var checks = control.querySelectorAll("input[data-multi-check='true']");
  for (var i = 0; i < checks.length; i++) {
    checks[i].checked = selected.indexOf(checks[i].value) >= 0;
  }

  var items = control.querySelectorAll("[data-multi-option]");
  for (var j = 0; j < items.length; j++) {
    var cb = items[j].querySelector("input[data-multi-check='true']");
    var isChecked = !!(cb && cb.checked);
    items[j].classList.toggle("border-primary", isChecked);
    items[j].classList.toggle("text-bg-primary", isChecked);
    items[j].classList.toggle("text-body", !isChecked);
  }

  var selectedHost = control.querySelector("[data-multi-selected='true']");
  if (selectedHost) {
    var event = new Event("change", { bubbles: true });
    var firstCheck = control.querySelector("input[data-multi-check='true']");
    if (firstCheck) firstCheck.dispatchEvent(event);
  }
}
module.exports.load = load;
